import {
  Injectable,
  inject,
  EnvironmentInjector,
  runInInjectionContext,
} from "@angular/core";
import {
  Database,
  ref,
  listVal,
  query,
  orderByChild,
  limitToLast,
  equalTo,
  set,
  get,
  update,
} from "@angular/fire/database";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Observable, of } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { AuthService } from "../../../providers/auth.service";
import {
  NotificationRecord,
  NotificationRenderer,
  NotificationRendererInput,
  RenderedNotification,
} from "./notification.model";

const NOTIFICATIONS_ROOT = "notifications";

@Injectable({ providedIn: "root" })
export class NotificationsService {
  private renderers = new Map<string, NotificationRenderer>();
  private renderersSubject = new BehaviorSubject<void>(undefined);

  private readonly mdb = inject(Database);
  private readonly injector = inject(EnvironmentInjector);

  constructor(
    private auth: AuthService,
    private translate: TranslateService
  ) {}

  registerRenderer(renderer: NotificationRenderer): void {
    this.renderers.set(renderer.type, renderer);
    this.renderersSubject.next();
  }

  list$(limit = 30): Observable<RenderedNotification[]> {
    return this.renderersSubject.pipe(
      switchMap(() => {
        const uid = this.auth.uid;
        if (!uid) return of([] as NotificationRecord[]);
        return runInInjectionContext(this.injector, () =>
          listVal<NotificationRecord>(
            query(
              ref(this.mdb, `${NOTIFICATIONS_ROOT}/${uid}`),
              orderByChild("createdAt"),
              limitToLast(limit)
            ),
            { keyField: "id" }
          )
        ).pipe(
          map((records) =>
            (records || []).sort(
              (a, b) => (b.createdAt || 0) - (a.createdAt || 0)
            )
          )
        );
      }),
      map((records) => records.map((r) => this.renderOne(r)))
    );
  }

  unreadCount$(): Observable<number> {
    const uid = this.auth.uid;
    if (!uid) return of(0);
    return runInInjectionContext(this.injector, () =>
      listVal<NotificationRecord>(
        query(
          ref(this.mdb, `${NOTIFICATIONS_ROOT}/${uid}`),
          orderByChild("readAt"),
          equalTo(null)
        )
      )
    ).pipe(map((records) => (records || []).length));
  }

  async markAsRead(notificationId: string): Promise<void> {
    const uid = this.auth.uid;
    if (!uid || !notificationId) return;
    await runInInjectionContext(this.injector, () =>
      set(
        ref(this.mdb, `${NOTIFICATIONS_ROOT}/${uid}/${notificationId}/readAt`),
        Date.now()
      )
    );
  }

  async markAllRead(): Promise<void> {
    const uid = this.auth.uid;
    if (!uid) return;
    const snap = await runInInjectionContext(this.injector, () =>
      get(
        query(
          ref(this.mdb, `${NOTIFICATIONS_ROOT}/${uid}`),
          orderByChild("readAt"),
          equalTo(null)
        )
      )
    );
    const updates: Record<string, number> = {};
    const now = Date.now();
    snap.forEach((child) => {
      updates[`${child.key}/readAt`] = now;
      return false;
    });
    if (Object.keys(updates).length === 0) return;
    await runInInjectionContext(this.injector, () =>
      update(ref(this.mdb, `${NOTIFICATIONS_ROOT}/${uid}`), updates)
    );
  }

  private renderOne(record: NotificationRecord): RenderedNotification {
    const renderer = this.renderers.get(record.type);
    const input: NotificationRendererInput = {
      record,
      translate: (key, params) =>
        this.translate.instant(key, params as Record<string, unknown>),
    };
    const fallbackTitle = record.title || record.type;
    const custom = renderer?.render?.(input);
    const title =
      custom?.title ||
      (renderer?.titleKey
        ? this.translate.instant(renderer.titleKey, { title: record.title })
        : fallbackTitle);
    const subtitle = custom?.subtitle ?? record.body;
    const route = custom?.route ?? record.refPath;
    return {
      id: record.id || "",
      type: record.type,
      icon: renderer?.icon || "notifications",
      title,
      subtitle,
      timestamp: record.createdAt,
      unread: !record.readAt,
      route,
      raw: record,
    };
  }
}
